import { getShippedQuantityForOrder } from "../utils/stockCalculations";

/**
 * Openstaande orderregels van één inkooporder: wat moet er nog geproduceerd of verzonden
 * worden (BMX-12). Voedt het overzicht in de modal, de Excel-export en de mail naar de
 * leverancier — alle drie uit deze ene berekening, zodat wat de gebruiker ziet exact is wat
 * de leverancier krijgt.
 *
 * Dit bestand bevat opzettelijk GEEN Firestore- of React-afhankelijkheden: alles hier is pure
 * logica die los te testen is (zie purchaseOrderOpenItems.test.js).
 *
 * De definitie van "open" is bewust dezelfde als achter de kolom "Verzonden / Open" op de
 * orderdetailpagina: besteld − verzonden − geannuleerd, nooit onder 0. Concept-verzendingen
 * tellen niet als verzonden (dat zit in `getShippedQuantityForOrder`, zie BMX-18): een concept
 * is werk in uitvoering en mag het openstaande aantal niet laten krimpen.
 */

/**
 * Het bestelde aantal van een regel: `adjustedAmount || amount`.
 *
 * LET OP — dit is bewust EXACT de bestaande berekening van de orderdetailpagina, inclusief de
 * `||`. Er staat hier geen `??`, en dat is geen slordigheid.
 *
 * Het verschil zit in precies één geval: een regel met `adjustedAmount: 0`. Met `||` valt die
 * terug op het oorspronkelijke `amount` (de regel blijft dus als open staan); met `??` zou hij
 * 0 open tonen. Dat tweede is de gevaarlijke kant op: een leverancier kan via het portaal een
 * aanpassing naar 0 vóórstellen (`min={0}` op het aantalveld), en zolang Bombyxx die niet heeft
 * goedgekeurd staat de order officieel gewoon nog op het oorspronkelijke aantal. Met `??` zou
 * het scherm dan "0 open" tonen voor een regel waarvan er nog 50 besteld staan — en dat is
 * precies het geval waarin er dubbel wordt ingekocht.
 *
 * Het CMS en het leveranciersportaal zijn het hier vandaag niet over eens: `supplier-portal/
 * lib/utils/orderQuantities.ts` gebruikt in zijn eigen `getOpenQuantity` wél `??`. Dat verschil
 * is echt en verdient een eigen beslissing, maar niet als bijvangst van een export-story.
 * Zie PURCHASE_FLOW_AUDIT.md §11 (BMX-12).
 */
export const getOrderedQuantity = (item) => item?.adjustedAmount || item?.amount || 0;

/** Wat er van deze regel nog open staat. */
export const getOpenQuantity = (item, shipments, orderId) => {
  const ordered = getOrderedQuantity(item);
  const shipped = getShippedQuantityForOrder(shipments, orderId, item?.productId);
  const cancelled = item?.cancelledAmount || 0;
  return Math.max(0, ordered - shipped - cancelled);
};

/**
 * De openstaande regels van een order, in dezelfde volgorde als op de order zelf. Regels die
 * volledig verzonden of volledig geannuleerd zijn vallen af — die hoeven niet nogmaals bij de
 * leverancier onder de aandacht gebracht te worden, en dat is precies het punt van het ticket
 * ("e-mail bevat alleen relevante open items voor die order").
 */
export const buildOpenItems = (order, shipments) => {
  if (!order) return [];
  return (order.products || [])
    .map((item) => ({
      productId: item.productId || null,
      productName: item.productName || "",
      productCode: item.productCode || "",
      productColor: item.productColor || "",
      priority: !!item.priority,
      ordered: getOrderedQuantity(item),
      shipped: getShippedQuantityForOrder(shipments, order.id, item.productId),
      cancelled: item.cancelledAmount || 0,
      open: getOpenQuantity(item, shipments, order.id),
    }))
    .filter((line) => line.open > 0);
};

/** Regels en stuks voor de kop van het overzicht en de totaalregel in de export. */
export const summarizeOpenItems = (openItems) =>
  (openItems || []).reduce(
    (acc, line) => ({ lines: acc.lines + 1, quantity: acc.quantity + (line.open || 0) }),
    { lines: 0, quantity: 0 },
  );
